import useMenu from '@lib/hooks/useMenu';
import useModal from '@lib/hooks/useModal';
import color from '@lib/utils/color';

import GenericButton from '@components/Button';
import { useDietFilterContext } from '@components/DietFilterProvider';
import Stack from '@components/Stack';
import StyledModal from '@components/StyledModal';
import Switch from '@components/Switch';
import Typography from '@components/Typography';
import { Diet } from '@lib/entities/Menu';
import { SxProps } from '@lib/hooks/useShorthandStyles';

export type OnChangeDietFilters = (filter: Diet, enabled: boolean) => void;

interface DietFilterModalProps {
    onClose?: () => void;
    onChange: OnChangeDietFilters;
}

export interface DietFilterModalComponent extends React.FC<DietFilterModalProps> {
    __fakePropertySoTheFormatterDoesntEraseThisInterface?: never;
}

export interface DietFilterModalButtonProps extends SxProps {
    onClose?: () => void;
    onOpen?: () => void;
}

export interface DietFilterModalComponent {
    Button: React.FC<DietFilterModalButtonProps>;
}

const DietFilterModal: DietFilterModalComponent = ({ onChange = () => {}, onClose = () => {} }) => {
    const { onClose: onCloseDietFilter } = useModal('dietFilter');
    const { diets, filters, setFilters } = useMenu();
    const { open, setOpen } = useDietFilterContext();

    const handleClose = () => {
        onCloseDietFilter();
        onClose();
        setOpen(false);
    };

    return (
        <StyledModal
            open={open}
            onClose={handleClose}
            style={{ zIndex: 100, width: '90%', maxWidth: 580, padding: 0, overflow: 'hidden' }}
        >
            <Stack sx={{ width: '100%' }}>
                <Stack
                    sx={{
                        base: {
                            backgroundColor: 'primary.main',
                            borderBottomColor: 'primary.dark',
                            borderBottomWidth: 2
                        },
                        sm: {
                            paddingHorizontal: 16,
                            paddingTop: 24,
                            paddingBottom: 24
                        },
                        md: {
                            paddingHorizontal: 34,
                            paddingTop: 32,
                            paddingBottom: 32
                        }
                    }}
                >
                    <Typography variant="t3" color="primary.dark">
                        Filter by Lifestyle
                    </Typography>
                </Stack>

                <Stack
                    sx={{
                        sm: { marginVertical: 8 },
                        md: { marginVertical: 16 }
                    }}
                >
                    {diets.map((dc, index) => (
                        <Stack
                            row
                            key={`diet-filter-${dc.id}`}
                            sx={{
                                base: {
                                    width: '100%',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    borderBottomColor: color('primary.dark/20'),
                                    borderBottomWidth: index === diets.length - 1 ? 0 : 1
                                },
                                sm: {
                                    paddingHorizontal: 16,
                                    paddingVertical: 8
                                },
                                md: {
                                    paddingHorizontal: 34,
                                    paddingVertical: 16
                                }
                            }}
                        >
                            <Stack gap={8}>
                                <Typography
                                    variant="p1"
                                    style={{ textAlign: 'left' }}
                                    semibold
                                    color="primary.dark"
                                >
                                    {dc.name}
                                </Typography>
                                {dc.description && (
                                    <Typography variant="p3">{dc.description}</Typography>
                                )}
                            </Stack>
                            <Switch
                                testID={`menu-diet-toggle-${dc.id}`}
                                accessibilityLabel={`${dc.name} filter`}
                                accessibilityHint={`Toggles whether menu items matching the ${dc.name} diet are shown`}
                                value={filters.includes(dc)}
                                onChange={(value) => {
                                    const nextFilters = value
                                        ? filters.concat(dc)
                                        : filters.filter((d) => d.id !== dc.id);
                                    onChange(dc, value);
                                    setFilters(nextFilters);
                                }}
                            />
                        </Stack>
                    ))}
                </Stack>

                <Stack
                    sx={{
                        sm: { paddingHorizontal: 16, paddingBottom: 16 },
                        md: { paddingHorizontal: 34, paddingBottom: 34 }
                    }}
                >
                    <GenericButton variant="primary.large" onPress={handleClose}>
                        DONE
                    </GenericButton>
                </Stack>
            </Stack>
        </StyledModal>
    );
};

export const Button: React.FC<DietFilterModalButtonProps> = ({
    onOpen = () => {},
    onClose = () => {}
}) => {
    const { open, setOpen } = useDietFilterContext();
    const { onOpen: onOpenDietFilter, onClose: onCloseDietFilter } = useModal('dietFilter');
    const { filters } = useMenu();

    const handlePress = () => {
        if (open) {
            setOpen(false);
            onCloseDietFilter();
            onClose();
            return;
        }
        setOpen(true);
        onOpenDietFilter();
        onOpen();
    };

    return (
        <GenericButton
            testID="menu-diet-filter-button"
            variant="primary.small"
            onPress={handlePress}
        >
            FILTERS{filters.length ? ` - ${filters.length}` : null}
        </GenericButton>
    );
};

DietFilterModal.Button = Button;

export default DietFilterModal;
